/**
 * =============================================================================
 * setera-web
 * =============================================================================
 * Copyright (c) 2025 HG Digital/Setera
 * Todos os direitos reservados.
 *
 * Este software é confidencial e de propriedade da HG Digital/Setera.
 * Seu uso está sujeito aos termos do contrato de licença.
 * -----
 * Criado em : 21-01-2025 10:00:00
 * Autor     : Cursor AI Assistant
 * Projeto   : setera-web
 * Versão    : 1.0.0
 * =============================================================================
 */

import { MatStepper } from "@angular/material/stepper";
import { STORAGE_KEY_ID_PERFIL } from "./../../storageCore/constStorageKeys";
import { ToolsService } from "./../../services/migrate/tools.service";
import { Component, OnInit, AfterViewInit } from "@angular/core";
import * as FileSaver from "file-saver";
import storageX from "../../storageCore/storageX";
import { Page } from "../../utils/paginate";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatAutocomplete } from "@angular/material/autocomplete";
import { FormControl } from "@angular/forms";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { CoreService } from "../../services/migrate/api.core.service";
import ApiService from "../../services/api.service";
import { TrackerFileService } from "../../services/tracker-file.service";
import { Empresa } from "../../models/markerModel";
import { Params } from "../../services/migrate/params";
import { USER_DATA } from "../../utils/constants.util";
import { DriverReportMapComponent } from "../driver-report/driver-report-map.component";
import { MatDialog } from "@angular/material/dialog";
import { ViewTrackerFileComponent } from "../tracker-file/view-tracker-file/view-tracker-file.component";

/**
 * Componente responsável pelo relatório de vídeo e foto
 * Permite filtrar e visualizar registros de mídia dos veículos
 */
@Component({
  selector: "app-video-photo-report",
  templateUrl: "./video-photo-report.component.html",
  styleUrls: ["./video-photo-report.component.scss"],
})
export class VideoPhotoReportComponent implements OnInit, AfterViewInit {
  public controlEmpresa = new FormControl();
  public controlVeiculo = new FormControl([]);
  public controlVeiculoInput = new FormControl();
  public selectedVehicles: any[] = [];

  public colsHeader = new Array<any>();
  public companys = new Array<any>();
  public companysFiltered = new Observable<Array<any>>();
  public vehicles = new Array<any>();
  public vehiclesFiltered = new Observable<Array<any>>();

  public empresaFilterSelected;
  public companiesFilter: any = [];
  public companiesSelect: any = [];

  private profileId = storageX.whereKeyIs(STORAGE_KEY_ID_PERFIL).get();
  public paramsLoad = {
    profileId: this.profileId,
    size: 9999,
    page: 0,
    sort: "nome",
    sortDirection: "ASC",
  };

  public consultaFormGroup: FormGroup;

  public serviceCore: CoreService;
  public serviceApi: ApiService;

  public pagination = new Page();
  public matStepper: MatStepper;
  public matAutocomplete: MatAutocomplete;
  public filterDialog = true;
  public items;

  public resultadoAnalitico = new Array<any>();

  // Lista estática de eventos válidos (mesma do tracker-file)
  private validSourceEvents = [
    'DISTRACTION',
    'SMOKING', 
    'PHONE_USE',
    'FRONTAL_COLISION',
    'LANE_DEPARTURE',
    'FRONTAL_PROXIMITY',
    'PEDESTRIAN_MOTORCYCLE_PROXIMITY',
    'FREQUENT_LANE_CHANGE',
    'OBSTACLE_PROXIMITY',
    'FADING_SLEEP',
    'DRIVER_ABSENT'
  ];

  constructor(
    private tools: ToolsService,
    apiserviceCore: CoreService,
    serviceApi: ApiService,
    private trackerFileService: TrackerFileService,
    public dialog: MatDialog,
    private formBuilder: FormBuilder
  ) {
    this.serviceCore = apiserviceCore;
    this.serviceApi = serviceApi;
  }

  ngAfterViewInit(): void {
    this.filterDialog = true;
  }

  ngOnInit() {
    this.pagination.currentPage = 0;
    this.pagination.size = 10;

    this.tools.builder().spinner.show();
    this.loadDataElements();

    this.consultaFormGroup = this.formBuilder.group({
      selecaoEmpresa: [""],
      dataInicio: ["", Validators.required],
      dataFim: ["", Validators.required],
      saltoGps: [true],
    });

    this.companysFiltered = this.controlEmpresa.valueChanges.pipe(
      map((value) => (typeof value === "string" ? value : value.id)),
      map((name) =>
        name ? this.filterValuesEmpresa(name) : this.companys.slice()
      )
    );

    this.vehiclesFiltered = this.controlVeiculoInput.valueChanges.pipe(
      map((value) => {
        if (typeof value === "string") {
          return value;
        } else if (value && value.cdPlaca) {
          return value.cdPlaca;
        }
        return "";
      }),
      map((name) =>
        name ? this.filterValuesVeiculo(name) : this.vehicles.slice()
      )
    );
  }

  /**
   * Filtra empresas baseado no valor digitado
   * @param value Valor para filtrar
   * @returns Array de empresas filtradas
   */
  public filterValuesEmpresa(value: any): Array<any> {
    const filterValue = value;

    if (this.controlEmpresa.value.cnpj != null) {
      if (value != null) {
        this.loadFilterCompany(value);
      }
    } else {
      if (value != null) {
        this.loadFilterDivision(value);
      }
    }

    return this.companys
      .sort((a, b) => a.nmNome.localeCompare(b.nmNome))
      .filter((client) => client.nmNome.toLowerCase().includes(filterValue));
  }

  /**
   * Exibe o nome da empresa no autocomplete
   * @param empresa Empresa selecionada
   * @returns Nome da empresa
   */
  displayEmpresa(empresa: Empresa): string {
    return empresa && empresa.nmNome ? empresa.nmNome : "";
  }

  /**
   * Filtra veículos baseado no valor digitado
   * @param value Valor para filtrar
   * @returns Array de veículos filtrados
   */
  public filterValuesVeiculo(value: string): Array<any> {
    const filterValue = value.toLowerCase();
    return this.vehicles
      .sort((a, b) => a.cdPlaca.localeCompare(b.cdPlaca))
      .filter(
        (vehicle) =>
          vehicle.cdPlaca.toLowerCase().includes(filterValue) ||
          vehicle.frota.toLowerCase().includes(filterValue)
      );
  }

  /**
   * Exibe placa/frota do veículo no autocomplete
   * @param vehicle Veículo selecionado
   * @returns String formatada placa/frota
   */
  displayVeiculo(vehicle: any): string {
    return vehicle && vehicle.cdPlaca
      ? `${vehicle.cdPlaca}/${vehicle.frota}`
      : "";
  }

  /**
   * Manipula seleção de veículo no autocomplete
   * @param event Evento de seleção
   */
  onVeiculoSelected(event: any): void {
    const vehicle = event.option.value;
    if (vehicle && !this.selectedVehicles.find((v) => v.id === vehicle.id)) {
      this.selectedVehicles.push(vehicle);
      this.controlVeiculo.setValue(this.selectedVehicles);
    }
    this.controlVeiculoInput.setValue("");
  }

  /**
   * Verifica se um veículo específico está selecionado
   * @param vehicle Veículo a ser verificado
   * @returns true se o veículo está selecionado
   */
  isVehicleSelected(vehicle: any): boolean {
    return this.selectedVehicles.some((selected) => selected.id === vehicle.id);
  }

  /**
   * Alterna a seleção de um veículo específico
   * @param vehicle Veículo a ser alternado
   */
  toggleVehicleSelection(vehicle: any): void {
    const index = this.selectedVehicles.findIndex((v) => v.id === vehicle.id);

    if (index > -1) {
      // Remove o veículo se já estiver selecionado
      this.selectedVehicles.splice(index, 1);
    } else {
      // Adiciona o veículo se não estiver selecionado
      this.selectedVehicles.push(vehicle);
    }

    this.controlVeiculo.setValue(this.selectedVehicles);

    // Força a detecção de mudanças
    setTimeout(() => {
      this.controlVeiculo.updateValueAndValidity();
    }, 0);
  }

  /**
   * Seleciona todos os veículos disponíveis
   */
  selectAllVehicles(): void {
    this.selectedVehicles = [...this.vehicles];
    this.controlVeiculo.setValue(this.selectedVehicles);
    this.controlVeiculoInput.setValue("");

    // Força a detecção de mudanças
    setTimeout(() => {
      this.controlVeiculo.updateValueAndValidity();
    }, 0);
  }

  /**
   * Alterna entre selecionar todos e deselecionar todos
   */
  toggleSelectAll(): void {
    if (this.isAllSelected()) {
      this.deselectAllVehicles();
    } else {
      this.selectAllVehicles();
    }
  }

  /**
   * Deseleciona todos os veículos
   */
  deselectAllVehicles(): void {
    this.selectedVehicles = [];
    this.controlVeiculo.setValue([]);
    this.controlVeiculoInput.setValue("");

    // Força a detecção de mudanças
    setTimeout(() => {
      this.controlVeiculo.updateValueAndValidity();
    }, 0);
  }

  /**
   * Verifica se todos os veículos estão selecionados
   * @returns true se todos os veículos estão selecionados
   */
  isAllSelected(): boolean {
    if (this.vehicles.length === 0) return false;

    // Verifica se todos os veículos disponíveis estão na lista de selecionados
    return this.vehicles.every((vehicle) =>
      this.selectedVehicles.some((selected) => selected.id === vehicle.id)
    );
  }

  /**
   * Verifica se alguns veículos estão selecionados (estado indeterminado)
   * @returns true se alguns veículos estão selecionados
   */
  isIndeterminate(): boolean {
    const selectedCount = this.vehicles.filter((vehicle) =>
      this.selectedVehicles.some((selected) => selected.id === vehicle.id)
    ).length;

    return selectedCount > 0 && selectedCount < this.vehicles.length;
  }

  /**
   * Remove um veículo da seleção
   * @param vehicle Veículo a ser removido
   */
  removeVehicle(vehicle: any): void {
    this.selectedVehicles = this.selectedVehicles.filter(
      (v) => v.id !== vehicle.id
    );
    this.controlVeiculo.setValue(this.selectedVehicles);

    // Força a detecção de mudanças
    setTimeout(() => {
      this.controlVeiculo.updateValueAndValidity();
    }, 0);
  }

  /**
   * Verifica se o formulário está válido para habilitar o botão consultar
   * @returns true se todos os campos obrigatórios estão preenchidos
   */
  isFormValid(): boolean {
    // Verifica se o formulário principal está válido
    const formValid = this.consultaFormGroup.valid;

    // Verifica se a empresa foi selecionada
    const empresaSelected =
      this.controlEmpresa.value != null && this.controlEmpresa.value != "";

    // Verifica se pelo menos um veículo foi selecionado
    const veiculoSelected = this.selectedVehicles.length > 0;

    return formValid && empresaSelected && veiculoSelected;
  }

  /**
   * Manipula paginação da tabela
   * @param event Evento de paginação
   */
  public paginate(event) {
    const params = this.tools.builder().params;

    this.pagination.currentPage = event.page;
    this.pagination.size = event.rows;
  }

  /**
   * Abre o dialog de filtros
   */
  public onOpenDialogFilter() {
    this.filterDialog = true;
  }

  /**
   * Exporta relatório para PDF
   */
  exportPdf() {
    // Função para carregar a imagem e converter para base64
    const loadImageAsBase64 = (url) => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.onload = () => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve(reader.result);
          };
          reader.readAsDataURL(xhr.response);
        };
        xhr.open("GET", url);
        xhr.responseType = "blob";
        xhr.send();
      });
    };

    // Função para adicionar o cabeçalho ao PDF
    const addHeader = (doc, imgData, data) => {
      const pageWidth = doc.internal.pageSize.width;

      // Dimensões da imagem
      const imgWidth = 40;
      const imgHeight = 40;

      const marginLeft = 200;
      const marginTop = 10;

      // Adicionando a imagem
      doc.addImage(imgData, "PNG", marginLeft, marginTop, imgWidth, imgHeight);

      const text = "SETERA - RELATÓRIO DE VÍDEO E FOTO";

      const textX = marginLeft + imgWidth + 10;
      const textY = marginTop + imgHeight / 2 + 5;

      doc.setFontSize(18);
      doc.setTextColor("#0000FF");
      doc.setFont("helvetica", "bold");
      doc.text(text, textX, textY);
    };

    if (this.resultadoAnalitico.length > 0) {
      import("jspdf").then((jsPDF) => {
        import("jspdf-autotable").then(() => {
          const doc = new jsPDF.default("l", "pt");

          loadImageAsBase64("assets/img/logo-aba.png")
            .then((imgData) => {
              const header = (data) => {
                addHeader(doc, imgData, data);
              };

              doc.autoTable({
                startY: 60,
                html: ".tableAnalitico",
                useCss: true,
                didDrawPage: header,
                margin: { top: 60 },
              });

              addHeader(doc, imgData, { settings: { margin: { left: 40 } } });

              doc.save(`relatorio_video_foto.pdf`);
            })
            .catch((error) => {
              console.error("Erro ao carregar a imagem:", error);
            });
        });
      });
    }
  }

  /**
   * Exporta relatório para Excel
   */
  exportExcel() {
    if (this.resultadoAnalitico.length > 0) {
      import("xlsx").then((xlsx) => {
        const worksheet = xlsx.utils.table_to_sheet(
          document.getElementsByClassName("tableAnalitico")[0],
          { dateNF: "mm/dd/yyyy hh:mm", cellDates: true }
        );
        const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
        const excelBuffer: any = xlsx.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });
        this.saveAsExcelFile(excelBuffer, `relatorio_video_foto`);
      });
    }
  }

  /**
   * Salva arquivo Excel
   * @param buffer Buffer do arquivo
   * @param fileName Nome do arquivo
   */
  saveAsExcelFile(buffer: any, fileName: string): void {
    let EXCEL_TYPE =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    let EXCEL_EXTENSION = ".xlsx";
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    FileSaver.saveAs(
      data,
      `relatorio_video_foto` +
        "_export_" +
        new Date().getTime() +
        EXCEL_EXTENSION
    );
  }

  /**
   * Abre mapa com coordenadas
   * @param lat Latitude
   * @param long Longitude
   */
  public openMap(lat, long) {
    const dialogRef = this.dialog.open(DriverReportMapComponent, {
      width: "450px",
      height: "450px",
      data: {
        lat: lat,
        lng: long,
      },
    });
  }

  /**
   * Executa consulta com os filtros aplicados
   */
  public onConsultar() {
    const params = new Params();

    const dataInicioRaw = this.consultaFormGroup.value.dataInicio;
    const dataFimRaw = this.consultaFormGroup.value.dataFim;

    // Converte para Date
    const dataInicio = new Date(dataInicioRaw);
    const dataFim = new Date(dataFimRaw);

    // Função auxiliar para garantir formatação completa
    const formatDateTime = (d: Date): string => {
      const pad = (n: number) => n.toString().padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
        d.getDate()
      )}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };

    const startDate = formatDateTime(dataInicio);
    const endDate = formatDateTime(dataFim);

    // Para múltiplos veículos, enviar array de IDs
    if (
      this.controlVeiculo.value != null &&
      this.controlVeiculo.value.length > 0
    ) {
      const vehicleIds = this.controlVeiculo.value.map(
        (vehicle: any) => vehicle.id
      );
      params.setParam("idTerminal", vehicleIds.join(","));
    }

    this.tools.builder().spinner.show();
    const terminalsIdsCsv = (this.controlVeiculo.value || [])
      .map((v: any) => v.id)
      .join(",");
    
    // Sempre enviar todos os eventos válidos
    const sourceEventsCsv = this.validSourceEvents.join(",");
    
    this.trackerFileService
      .getReportsFiles(startDate, endDate, terminalsIdsCsv, sourceEventsCsv)
      .subscribe((files: any[]) => {
        this.resultadoAnalitico = files || [];
        this.tools.builder().spinner.hide();
        this.filterDialog = false;

        if (this.resultadoAnalitico.length == 0) {
          this.tools
            .builder()
            .openToast.emitInf("Nenhum resultado para exibir no momento.");
        }
      });
  }

  /**
   * Carrega dados iniciais (empresas, veículos, motoristas)
   */
  public async loadDataElements() {
    try {
      this.tools.builder().spinner.show();

      const companys = await this.serviceCore.get<any>({
        url: "v2/companys",
        params: {
          profileId: USER_DATA().profileId,
          size: "5000",
          page: "0",
          sort: "nome",
          sortDirections: "ASC",
        },
      });

      this.companys = companys?.elements;
      this.companys = this.companys.sort((a, b) =>
        a.nmNome.localeCompare(b.nmNome)
      );
      this.controlEmpresa.setValue("");

      const terminals = await this.serviceCore.get<any>({
        url: "v2/terminals",
        params: {
          profileId: USER_DATA().profileId,
          size: "5000",
          page: "0",
          sortDirections: "ASC",
        },
      });

      this.vehicles = terminals.elements;
      this.vehicles = this.vehicles.sort((a, b) =>
        a.cdPlaca.localeCompare(b.cdPlaca)
      );
      this.controlVeiculo.setValue([]);
      this.controlVeiculoInput.setValue("");
      this.selectedVehicles = [];

      this.companiesSelect = [
        { name: "Divisão", value: 1 },
        { name: "Matriz", value: 2 },
        { name: "Filial", value: 3 },
      ];

      this.tools.builder().spinner.hide();
    } catch (error) {
      this.tools.builder().spinner.hide();
      throw new EvalError(error);
    }
  }

  /**
   * Carrega filtros baseados na empresa selecionada
   * @param value ID da empresa
   */
  public async loadFilterCompany(value: any) {
    try {
      this.tools.builder().spinner.show();

      const terminals = await this.serviceCore.get<any>({
        url: "v2/terminals/findByCompany",
        params: {
          profileId: USER_DATA().profileId,
          idEmpresa: value,
          size: "5000",
          page: "0",
          sortDirections: "ASC",
        },
      });

      this.vehicles = terminals.elements;
      this.vehicles = this.vehicles.sort((a, b) =>
        a.cdPlaca.localeCompare(b.cdPlaca)
      );

      this.controlVeiculo.setValue([]);
      this.controlVeiculoInput.setValue("");
      this.selectedVehicles = [];

      this.tools.builder().spinner.hide();
    } catch (error) {
      this.tools.builder().spinner.hide();
      throw new EvalError(error);
    }
  }

  /**
   * Carrega filtros baseados na divisão selecionada
   * @param value ID da divisão
   */
  public async loadFilterDivision(value: any) {
    try {
      this.tools.builder().spinner.show();

      const terminals = await this.serviceCore.get<any>({
        url: "v2/terminals/findByDivision",
        params: {
          profileId: USER_DATA().profileId,
          idDivisao: value,
          size: "5000",
          page: "0",
          sortDirections: "ASC",
        },
      });

      this.vehicles = terminals.elements;
      this.vehicles = this.vehicles.sort((a, b) =>
        a.cdPlaca.localeCompare(b.cdPlaca)
      );

      this.controlVeiculo.setValue([]);
      this.controlVeiculoInput.setValue("");
      this.selectedVehicles = [];

      this.tools.builder().spinner.hide();
    } catch (error) {
      this.tools.builder().spinner.hide();
      throw new EvalError(error);
    }
  }

  /**
   * Filtra empresas por tipo (Matriz/Filial/Divisão)
   */
  public async filterCompanyReports() {
    this.controlEmpresa.setValue("");
    var idEmpresa = this.empresaFilterSelected;
    var empresas: any = [];

    if (idEmpresa == "Divisão") {
      this.companys.forEach((element) => {
        if (element.cnpj == null) {
          empresas.push(element);
        }
      });
    }

    if (idEmpresa == "Matriz") {
      this.companys.forEach((element) => {
        if (element.cnpj != null && element.idEmpresaMatriz == null) {
          empresas.push(element);
        }
      });
    }

    if (idEmpresa == "Filial") {
      this.companys.forEach((element) => {
        if (element.cnpj != null && element.idEmpresaMatriz != null) {
          empresas.push(element);
        }
      });
    }

    this.companiesFilter = empresas;
    this.companysFiltered.subscribe(this.companiesFilter);
  }

  /**
   * Visualiza mídia (vídeo ou foto) em modal
   * @param rowdata Dados da linha selecionada
   */
  public visualizarMidia(rowdata: any): void {
    // Implementar modal para visualização de mídia
    // Pode usar um dialog do Angular Material ou abrir em nova aba
    if (rowdata.tipoMidia === "VIDEO") {
      // Abrir vídeo em nova aba ou modal
      window.open(rowdata.urlMidia, "_blank");
    } else {
      // Abrir foto em nova aba ou modal
      window.open(rowdata.urlMidia, "_blank");
    }
  }

  /**
   * Abre o mesmo popup de visualização usado no módulo tracker-file, a partir de uma linha desta tabela
   */
  public viewFromRow(rowdata: any): void {
    // Cria um objeto compatível com o ViewTrackerFileComponent
    const trackerFileData: any = {
      fileLink: rowdata.fileLink,
      fileName: rowdata.fileName,
      plate: rowdata.plate,
      date: rowdata.date,
      camera: rowdata.camera,
      event: rowdata.event,
      latitude: rowdata.latitude,
      longitude: rowdata.longitude,
    };

    // Abre diretamente o dialog com os dados, sem precisar do getLink
    const dialogRef = this.dialog.open(ViewTrackerFileComponent, {
      data: trackerFileData,
    });
    dialogRef.afterClosed().subscribe((result) => {});
  }
}
